/**
 * MyRequestsPage — every signed-in user can see the status of every
 * change request they have personally submitted, from `pending` all
 * the way to `deployed` (or `declined`). Read-only — no admin
 * actions are exposed here. For the admin triage page see
 * /admin/requested-changes.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Lightbulb, ArrowsClockwise, CheckCircle, Wrench, Hammer, Rocket,
  Eye, X, Paperclip, DownloadSimple, Sparkle, ChatCircleDots, Clock,
  ShieldStar, ClockClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";
import SuggestChangeButton from "@/components/SuggestChangeButton";

const STATUS_ORDER = [
  "pending", "approved_to_build", "under_construction",
  "built_pending_deploy", "deployed", "declined",
];
const STATUS_LABEL = {
  pending: "Pending review",
  approved_to_build: "Approved · sent to build",
  under_construction: "Being built",
  built_pending_deploy: "Built · awaiting redeploy",
  deployed: "Live in production",
  declined: "Declined",
};
const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  approved_to_build: "bg-blue-100 text-blue-900 border-blue-300",
  under_construction: "bg-violet-100 text-violet-900 border-violet-300",
  built_pending_deploy: "bg-orange-100 text-orange-900 border-orange-300",
  deployed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  declined: "bg-slate-200 text-slate-600 border-slate-300",
};
const STATUS_ICON = {
  pending: <Clock size={11} weight="fill" />,
  approved_to_build: <CheckCircle size={11} weight="fill" />,
  under_construction: <Wrench size={11} weight="fill" />,
  built_pending_deploy: <Hammer size={11} weight="fill" />,
  deployed: <Rocket size={11} weight="fill" />,
  declined: <X size={11} weight="bold" />,
};

function _ago(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function MyRequestsPage({ user, onLogout, onLogoutAll }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [suggestSignal, setSuggestSignal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/suggestions/mine");
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load your requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Refresh once shortly after the page mounts so a freshly-submitted
  // request shows up without the user having to manually refresh.
  useEffect(() => {
    const t = setTimeout(() => load(), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestSignal]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.status === filter);
  }, [items, filter]);

  const total = items.length;
  const liveCount = counts.deployed || 0;
  const inFlightCount =
    (counts.approved_to_build || 0) + (counts.under_construction || 0) + (counts.built_pending_deploy || 0);
  const pendingCount = counts.pending || 0;

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="my-requests-page">
      <AdminPageHeader
        sticky maxWidth="max-w-4xl" testId="my-requests-back"
        icon={<Lightbulb size={20} weight="fill" className="text-amber-600" />}
        title="My requested changes"
        subtitle="Status of every change request you have submitted to the portal."
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
      />

      {/* Hidden trigger so we can programmatically open the modal from the empty-state CTA */}
      <SuggestChangeButton hideTrigger openSignal={suggestSignal} />

      <main className="max-w-4xl mx-auto p-6 pb-32 space-y-5">
        {/* Summary stat strip */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="my-requests-stats">
          <StatPill label="Total" value={total} tone="slate" testId="my-requests-stat-total"
            tip="Every change request you have ever submitted, including declined and live ones." />
          <StatPill label="Pending" value={pendingCount} tone="amber" testId="my-requests-stat-pending"
            tip="Submitted and waiting for a super-admin to review." />
          <StatPill label="In flight" value={inFlightCount} tone="blue" testId="my-requests-stat-inflight"
            tip="Approved and being built — either sent to engineering, under construction, or in preview waiting on the next deploy." />
          <StatPill label="Live" value={liveCount} tone="emerald" testId="my-requests-stat-live"
            tip="Deployed to production — your change is live on the portal." />
        </section>

        {/* Toolbar — filter chips + actions */}
        <section className="flex flex-wrap items-center gap-2" data-testid="my-requests-filters">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")}
                      label="All" count={total} testId="my-requests-filter-all" />
          {STATUS_ORDER.map((s) => (
            <FilterChip key={s}
                        active={filter === s}
                        onClick={() => setFilter(s)}
                        label={STATUS_LABEL[s]}
                        count={counts[s] || 0}
                        testId={`my-requests-filter-${s}`}
                        disabled={(counts[s] || 0) === 0} />
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={load} disabled={loading}
                    data-testid="my-requests-refresh"
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-100">
              <ArrowsClockwise size={11} weight="fill" className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button onClick={() => setSuggestSignal((n) => n + 1)}
                    data-testid="my-requests-new"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-amber-500 hover:bg-amber-600 text-white border border-amber-600 shadow-sm">
              <Lightbulb size={11} weight="fill" /> + New request
            </button>
          </div>
        </section>

        {/* Empty / loading / list */}
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="my-requests-loading">
            <ArrowsClockwise size={20} weight="bold" className="animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState filter={filter} hasAny={total > 0}
                      onNew={() => setSuggestSignal((n) => n + 1)} />
        ) : (
          <ul className="space-y-3" data-testid="my-requests-list">
            {filtered.map((it) => (
              <RequestRow key={it.id} it={it}
                          expanded={!!expanded[it.id]}
                          onToggle={() => setExpanded((p) => ({ ...p, [it.id]: !p[it.id] }))} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

const STAT_TONE = {
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  amber: "bg-amber-50 border-amber-300 text-amber-900",
  blue: "bg-blue-50 border-blue-300 text-blue-900",
  emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
};
function StatPill({ label, value, tone, testId, tip }) {
  return (
    <div data-testid={testId}
         title={tip || undefined}
         className={`rounded-md border px-3 py-2 ${STAT_TONE[tone] || STAT_TONE.slate} ${tip ? "cursor-help" : ""}`}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider mt-1 opacity-75">{label}</div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, testId, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
            data-testid={testId}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
              active
                ? "bg-slate-900 text-white border-slate-900"
                : disabled
                  ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
            }`}>
      {label}
      {count > 0 && (
        <span className={`ml-1 px-1 rounded text-[9px] ${active ? "bg-white/20" : "bg-slate-100"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function EmptyState({ filter, hasAny, onNew }) {
  return (
    <div className="border-2 border-dashed border-slate-300 rounded-md py-16 px-6 text-center bg-white"
         data-testid="my-requests-empty">
      <Lightbulb size={32} weight="fill" className="mx-auto text-amber-400 mb-3" />
      {hasAny ? (
        <>
          <p className="text-sm font-semibold text-slate-700">No requests in this status</p>
          <p className="text-xs text-slate-500 mt-1">
            Try a different filter — or pick <strong>All</strong>.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm font-semibold text-slate-700">You haven't submitted any change requests yet</p>
          <p className="text-xs text-slate-500 mt-1">
            See something you'd like improved? Submit a request — a super-admin will review it.
          </p>
          <button onClick={onNew}
                  data-testid="my-requests-empty-new"
                  className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-amber-500 hover:bg-amber-600 text-white border border-amber-600">
            <Lightbulb size={12} weight="fill" /> Submit your first request
          </button>
        </>
      )}
    </div>
  );
}

function RequestRow({ it, expanded, onToggle }) {
  const att = it.attachments || [];
  const ai = it.ai_assessment;
  const history = Array.isArray(it.history) ? it.history : [];
  return (
    <li className="bg-white border border-slate-200 rounded-md p-4 shadow-sm"
        data-testid={`my-request-${it.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span data-testid={`my-request-status-${it.id}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${STATUS_TONE[it.status] || STATUS_TONE.pending}`}>
              {STATUS_ICON[it.status]}
              {STATUS_LABEL[it.status] || it.status}
            </span>
            <span className="text-[10px] text-slate-500">{_ago(it.submitted_at)}</span>
            {att.length > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-slate-600 border border-slate-200 rounded-sm">
                <Paperclip size={10} weight="fill" /> {att.length}
              </span>
            )}
          </div>
          <h3 className="text-sm font-bold text-slate-900 break-words">{it.subject}</h3>
        </div>
      </div>

      {/* AI assessment chip (read-only context for the submitter) */}
      {ai && (ai.risk || ai.summary) && (
        <AiChip ai={ai} sid={it.id} />
      )}

      {/* Helpful status copy */}
      <StatusBlurb it={it} />

      <p className="mt-2 text-[13px] text-slate-700 whitespace-pre-wrap break-words">
        {expanded || (it.body || "").length <= 220 ? it.body : `${(it.body || "").slice(0, 220)}…`}
      </p>
      {(it.body || "").length > 220 && (
        <button onClick={onToggle}
                data-testid={`my-request-toggle-${it.id}`}
                className="mt-1 text-[11px] font-bold text-blue-700 hover:text-blue-900 uppercase tracking-wider">
          {expanded ? "Show less" : "Show more"}
        </button>
      )}

      {/* Attachments — download chips */}
      {att.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`my-request-attachments-${it.id}`}>
          {att.map((a) => {
            const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            const url = `${apiBase}${a.url}`;
            return (
              <a key={a.id} href={url} target="_blank" rel="noopener noreferrer"
                 data-testid={`my-request-attachment-${a.id}`}
                 className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-100">
                <DownloadSimple size={10} weight="fill" /> {a.filename}
              </a>
            );
          })}
        </div>
      )}

      {/* Preview link if available */}
      {it.preview_url && it.status === "built_pending_deploy" && (
        <a href={it.preview_url} target="_blank" rel="noopener noreferrer"
           data-testid={`my-request-preview-${it.id}`}
           className="mt-3 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-orange-300 text-orange-700 hover:bg-orange-50">
          <Eye size={10} weight="fill" /> Open preview
        </a>
      )}

      {/* Decline reason — surface clearly if the request was declined */}
      {it.status === "declined" && (it.decline_reason || "").trim() && (
        <div className="mt-3 rounded-sm border border-rose-200 bg-rose-50 p-2.5"
             data-testid={`my-request-decline-${it.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1">
            <X size={10} weight="bold" /> Decline reason
          </div>
          <p className="text-[12px] text-rose-900 mt-1 whitespace-pre-wrap">{it.decline_reason}</p>
        </div>
      )}

      {/* History timeline (collapsible) */}
      {history.length > 1 && (
        <details className="mt-3 group">
          <summary data-testid={`my-request-history-toggle-${it.id}`}
                   className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
            <ClockClockwise size={11} weight="fill" /> History ({history.length})
          </summary>
          <ol className="mt-2 space-y-1 border-l-2 border-slate-200 pl-3"
              data-testid={`my-request-history-${it.id}`}>
            {history.slice().reverse().map((h, i) => (
              <li key={i} className="text-[11px] text-slate-600">
                <span className="text-slate-400 font-mono">{_ago(h.at)}</span>{" "}
                <span className="font-semibold text-slate-700">{STATUS_LABEL[h.to] || h.to}</span>
                {h.note && <span className="text-slate-500"> — {h.note}</span>}
              </li>
            ))}
          </ol>
        </details>
      )}
    </li>
  );
}

const AI_TONE = {
  low: "bg-emerald-50 border-emerald-300 text-emerald-900",
  medium: "bg-amber-50 border-amber-300 text-amber-900",
  high: "bg-rose-50 border-rose-300 text-rose-900",
};
function AiChip({ ai, sid }) {
  const tone = AI_TONE[ai.risk] || "bg-slate-50 border-slate-200 text-slate-700";
  const riskLabel = ai.risk ? `${ai.risk} risk` : "Needs review";
  return (
    <div className={`mt-3 rounded-sm border p-2.5 ${tone}`}
         data-testid={`my-request-ai-${sid}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
        <Sparkle size={11} weight="fill" /> AI assessment · {riskLabel}
      </div>
      {ai.summary && (
        <p className="mt-1 text-[12px] leading-relaxed">{ai.summary}</p>
      )}
      {Array.isArray(ai.suggested_areas) && ai.suggested_areas.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {ai.suggested_areas.map((a) => (
            <span key={a} className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-white/60 border border-current/20">
              {a}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_BLURB = {
  pending: {
    icon: <ShieldStar size={11} weight="fill" className="text-blue-700" />,
    text: "Awaiting a super-admin to review and approve.",
  },
  approved_to_build: {
    icon: <PaperPlane />,
    text: "Approved! The engineering agent has been notified to start work.",
  },
  under_construction: {
    icon: <Wrench size={11} weight="fill" className="text-violet-700" />,
    text: "Being built right now — check back shortly.",
  },
  built_pending_deploy: {
    icon: <Hammer size={11} weight="fill" className="text-orange-700" />,
    text: "Built in preview. Waiting on the next production deploy + test-admin approval.",
  },
  deployed: {
    icon: <Rocket size={11} weight="fill" className="text-emerald-700" />,
    text: "Live in production. Thanks for the suggestion!",
  },
  declined: {
    icon: <X size={11} weight="bold" className="text-slate-500" />,
    text: "Declined — see the reason below.",
  },
};
function PaperPlane() {
  // Tiny local shim so we don't have to import another phosphor icon for a single use.
  return <ChatCircleDots size={11} weight="fill" className="text-blue-700" />;
}
function StatusBlurb({ it }) {
  const b = STATUS_BLURB[it.status];
  if (!b) return null;
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-600"
         data-testid={`my-request-blurb-${it.id}`}>
      {b.icon}
      <span>{b.text}</span>
    </div>
  );
}
